<template>
    <div :class="fullWidth ? 'chat-response-full-width' : 'chat-response-bubble'">
        <!-- Copy Icon -->
        <button
            type="button"
            class="copy-icon-btn"
            v-clipboard:copy="copyText"
            v-clipboard:success="onCopySuccess"
            v-clipboard:error="onCopyError"
            title="Copy response to clipboard"
        >
            <i class="fas fa-copy"></i>
        </button>

        <!-- Response Content -->
        <div class="response-bubble-content">
            <div class="answer-text">
                <span v-html="displayedAnswer"></span>
                <span v-if="isTyping" class="typing-cursor">|</span>
            </div>

            <!-- Metadata (if enabled) -->
            <div v-if="showMetadata" class="metadata-section mt-2">
                <small class="text-muted">
                    <div class="d-flex flex-wrap gap-2">
                        <span v-if="response.cached" class="badge badge-info">
                            <i class="fas fa-save me-1"></i> Cached
                        </span>
                        <span v-if="response.tokensUsed" class="badge badge-light">
                            {{ response.tokensUsed }} tokens
                        </span>
                        <span v-if="response.model" class="badge badge-light">
                            {{ response.model }}
                        </span>
                    </div>
                </small>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ResponseDisplay',

    props: {
        response: {
            type: Object,
            required: true
        },
        showMetadata: {
            type: Boolean,
            default: false
        },
        isLatest: {
            type: Boolean,
            default: false
        },
        typewriterEffect: {
            type: Boolean,
            default: false
        },
        fullWidth: {
            type: Boolean,
            default: false
        }
    },

    data() {
        return {
            displayedText: '',
            typewriterInterval: null,
            isTyping: false
        }
    },

    computed: {
        formattedAnswer() {
            if (!this.response.answer) return ''

            let formatted = this.response.answer

            // Convert markdown links to HTML links
            formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

            // Convert markdown bold text to HTML strong tags
            formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

            // Convert markdown italic text to HTML em tags
            formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>')

            // Basic formatting: convert line breaks to HTML
            formatted = formatted
                .replace(/\n\n/g, '</p><p>')
                .replace(/\n/g, '<br>')

            // Wrap in paragraph tags if not already wrapped
            if (!formatted.startsWith('<p>')) {
                formatted = '<p>' + formatted + '</p>'
            }

            return formatted
        },

        copyText() {
            return this.response.answer
        },

        displayedAnswer() {
            if (!this.typewriterEffect || !this.isTyping) {
                return this.formattedAnswer
            }
            return this.formatText(this.displayedText)
        },

        plainTextAnswer() {
            return this.response.answer || ''
        }
    },

    mounted() {
        if (this.typewriterEffect && this.response.answer) {
            this.startTypewriter()
        }
    },

    beforeDestroy() {
        this.stopTypewriter()
    },

    watch: {
        'response.answer': {
            handler(newAnswer) {
                if (this.typewriterEffect && newAnswer) {
                    this.startTypewriter()
                }
            },
            immediate: true
        }
    },

    methods: {
        formatText(text) {
            if (!text) return ''

            let formatted = text

            // Convert markdown links to HTML links
            formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>')

            // Convert markdown bold text to HTML strong tags
            formatted = formatted.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')

            // Convert markdown italic text to HTML em tags
            formatted = formatted.replace(/\*([^*]+)\*/g, '<em>$1</em>')

            // Basic formatting: convert line breaks to HTML
            formatted = formatted
                .replace(/\n\n/g, '</p><p>')
                .replace(/\n/g, '<br>')

            // Wrap in paragraph tags if not already wrapped
            if (!formatted.startsWith('<p>')) {
                formatted = '<p>' + formatted + '</p>'
            }

            return formatted
        },

        startTypewriter() {
            if (!this.plainTextAnswer) return
            
            this.stopTypewriter()
            this.displayedText = ''
            this.isTyping = true
            
            let currentIndex = 0
            const text = this.plainTextAnswer
            let speed = 20 // milliseconds per character (faster)
            const speedupTime = 3000 // 3 seconds
            const fastSpeed = 5 // faster speed after 3 seconds
            
            const startTime = Date.now()
            
            this.typewriterInterval = setInterval(() => {
                // Speed up after 3 seconds
                const elapsed = Date.now() - startTime
                if (elapsed > speedupTime) {
                    speed = fastSpeed
                    clearInterval(this.typewriterInterval)
                    
                    // Restart with faster speed
                    this.typewriterInterval = setInterval(() => {
                        if (currentIndex < text.length) {
                            this.displayedText = text.substring(0, currentIndex + 1)
                            currentIndex++
                            this.scrollIntoView()
                        } else {
                            this.stopTypewriter()
                            this.isTyping = false
                        }
                    }, fastSpeed)
                } else if (currentIndex < text.length) {
                    this.displayedText = text.substring(0, currentIndex + 1)
                    currentIndex++
                    this.scrollIntoView()
                } else {
                    this.stopTypewriter()
                    this.isTyping = false
                }
            }, speed)
        },

        stopTypewriter() {
            if (this.typewriterInterval) {
                clearInterval(this.typewriterInterval)
                this.typewriterInterval = null
            }
        },

        scrollIntoView() {
            // Throttle scroll calls to avoid too frequent scrolling
            if (this._scrollTimeout) return
            
            this._scrollTimeout = setTimeout(() => {
                this._scrollTimeout = null
                if (this.$el && this.$el.scrollIntoView) {
                    this.$el.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'end',
                        inline: 'nearest' 
                    })
                }
            }, 50)
        },

        onCopySuccess(e) {
            this.$emit('copy', e.text)
        },

        onCopyError(e) {
            console.error('Copy failed:', e)
        }
    }
}
</script>

<style scoped>
.chat-response-bubble {
    position: relative;
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 18px;
    border-bottom-left-radius: 6px;
    padding: 0.6rem 0.85rem;
    margin-bottom: 0.5rem;
    word-wrap: break-word;
    box-shadow: 0 1px 2px rgba(0,0,0,0.1);
    max-width: 100%;
}

.chat-response-full-width {
    position: relative;
    background: transparent;
    border: none;
    padding: 0;
    margin-bottom: 1rem;
    word-wrap: break-word;
    width: 100%;
}

.copy-icon-btn {
    position: absolute;
    bottom: -8px;
    right: 8px;
    background: #fff;
    border: 1px solid #e9ecef;
    color: #6c757d;
    cursor: pointer;
    padding: 0.25rem;
    border-radius: 50%;
    font-size: 0.75rem;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
    opacity: 0;
}

.chat-response-bubble:hover .copy-icon-btn,
.chat-response-full-width:hover .copy-icon-btn {
    opacity: 1;
}

.chat-response-full-width .copy-icon-btn {
    bottom: -8px;
    right: 8px;
}

.copy-icon-btn:hover {
    background: #f8f9fa;
    color: #495057;
    transform: scale(1.1);
}

.response-bubble-content {
    background: transparent;
}

.answer-text {
    color: #212529;
    line-height: 1.6;
    font-size: 0.9rem;
}

.chat-response-bubble >>> .answer-text p {
    margin: 0 !important;
    padding: 0 !important;
}

.answer-text >>> p {
    margin: 0 !important;
    padding: 0 !important;
}

.chat-response-bubble .answer-text p {
    margin: 0 !important;
    padding: 0 !important;
}

.answer-text p {
    margin: 0 !important;
    padding: 0 !important;
}

.answer-text a {
    color: #007bff;
    text-decoration: none;
    font-weight: 500;
}

.answer-text a:hover {
    color: #0056b3;
    text-decoration: underline;
}

.typing-cursor {
    animation: blink 1s infinite;
    font-weight: bold;
    color: #007bff;
}

@keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
}

.metadata-section {
    border-top: 1px solid #f0f0f0;
    padding-top: 0.5rem;
}

.badge {
    margin-right: 0.25rem;
    margin-bottom: 0.25rem;
    font-size: 0.65rem;
}

.gap-2 > * {
    margin-right: 0.5rem;
    margin-bottom: 0.25rem;
}

@media (max-width: 576px) {
    .chat-response-bubble {
        padding: 0.5rem 0.65rem;
        font-size: 0.85rem;
    }

    .copy-icon-btn {
        top: -6px;
        right: -6px;
        width: 20px;
        height: 20px;
        font-size: 0.7rem;
    }

    .answer-text {
        font-size: 0.85rem;
    }
}
</style>



// WEBPACK FOOTER //
// src/Assets/components/AiAgents/CustomerService/components/ResponseDisplay.vue